import { useEffect, useMemo, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Users, Plus, Search, Edit2, Trash2, Upload, Loader2, Link2, Sparkles,
  Tag,
  CheckCircle2, AlertCircle, Wallet, Square, CheckSquare, AlertTriangle,
  Receipt, Building2,
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import MultiPersonExpenseDialog from '@/components/MultiPersonExpenseDialog';

const LINK_TYPES = [
  { key: 'supplier', label: 'Supplier', color: 'bg-orange-100 text-orange-700 border-orange-200' },
  { key: 'customer', label: 'Customer', color: 'bg-info/10 text-info border-info/30' },
  { key: 'employee', label: 'Employee', color: 'bg-success/10 text-success border-success/30' },
  { key: 'partner', label: 'Partner', color: 'bg-purple-100 text-purple-700 border-purple-200' },
  { key: 'expense_category', label: 'Expense Cat.', color: 'bg-warning/10 text-warning border-warning/30' },
  { key: 'platform', label: 'Online Platform', color: 'bg-rose-100 text-rose-700 border-rose-200' },
  // iter386 — semantic tags for non-entity payees (no entity-picker needed)
  { key: 'government', label: 'Government', color: 'bg-red-100 text-red-700 border-red-200', tagOnly: true },
  { key: 'utility', label: 'Utility', color: 'bg-sky-100 text-sky-700 border-sky-200', tagOnly: true },
  { key: 'other', label: 'Other', color: 'bg-stone-100 text-stone-700 border-stone-200', tagOnly: true },
];
const LINK_MAP = Object.fromEntries(LINK_TYPES.map(t => [t.key, t]));

const emptyForm = {
  name: '', iban: '', account_number: '', bank: '', link_type: '', link_id: '', notes: '',
};

export default function BeneficiariesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterLink, setFilterLink] = useState('all');
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [linkOptions, setLinkOptions] = useState([]);
  const [importing, setImporting] = useState(false);
  const [showMatch, setShowMatch] = useState(false);
  const [matchLoading, setMatchLoading] = useState(false);
  const [matchData, setMatchData] = useState(null);
  const [selectedMatches, setSelectedMatches] = useState({});
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showSplitDialog, setShowSplitDialog] = useState(false);
  const [splitFor, setSplitFor] = useState({ beneficiaryId: null, defaultCategory: '' });
  // iter395n — Inline suggestion map: beneficiary_id → top_match.
  // Loaded once on mount via /beneficiaries/match-suggestions so the
  // table can show a "Looks like X (95%)" chip beside each unlinked row.
  // Clicking the chip opens a one-step Link confirmation dialog instead
  // of forcing the user through the multi-step Match Suggestions wizard.
  const [inlineSuggestions, setInlineSuggestions] = useState({});
  const [confirmLink, setConfirmLink] = useState(null);
  // {beneficiary, match} — when set, renders the confirm dialog.
  // iter469 — Bank-code patterns dialog state.
  const [patternsFor, setPatternsFor] = useState(null); // beneficiary row whose patterns we're editing

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = (rows) => {
    setSelectedIds(prev => {
      const allIds = rows.map(r => r.id);
      const allSelected = allIds.every(id => prev.has(id)) && allIds.length > 0;
      return allSelected ? new Set() : new Set(allIds);
    });
  };
  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} beneficiaries? This cannot be undone.`)) return;
    try {
      const res = await api.post('/beneficiaries/bulk-delete', { ids });
      toast.success(`Deleted ${res.data.deleted} beneficiaries`);
      setSelectedIds(new Set());
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Bulk delete failed'); }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/beneficiaries');
      setItems(res.data || []);
    } catch {
      toast.error('Failed to load beneficiaries');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  // iter395n — Pre-load suggestions silently so each unlinked row gets a
  // tiny "Looks like supplier X — 92%" chip without user action.
  useEffect(() => {
    api.get('/beneficiaries/match-suggestions')
      .then((res) => {
        const map = {};
        for (const s of (res.data?.suggestions || [])) {
          if (s.top_matches?.[0]) map[s.beneficiary_id] = s.top_matches[0];
        }
        setInlineSuggestions(map);
      })
      .catch(() => { /* non-fatal — bulk modal still works */ });
  }, []);

  useEffect(() => {
    const t = form.link_type;
    if (!t) { setLinkOptions([]); return; }
    api.get(`/beneficiaries/link-options/${t}`)
      .then(r => setLinkOptions(r.data || []))
      .catch(() => setLinkOptions([]));
  }, [form.link_type]);

  const openNew = () => {
    setEditing(null);
    setForm({ ...emptyForm });
    setShowDialog(true);
  };

  const openEdit = (b) => {
    setEditing(b);
    setForm({
      name: b.name || '',
      iban: b.iban || '',
      account_number: b.account_number || '',
      bank: b.bank || '',
      link_type: b.link_type || '',
      link_id: b.link_id || '',
      notes: b.notes || '',
    });
    setShowDialog(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    try {
      const payload = {
        ...form,
        link_type: form.link_type || null,
        link_id: form.link_id || null,
      };
      if (editing) {
        await api.patch(`/beneficiaries/${editing.id}`, payload);
        toast.success('Beneficiary updated');
      } else {
        await api.post('/beneficiaries', payload);
        toast.success('Beneficiary created');
      }
      setShowDialog(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this beneficiary?')) return;
    try {
      await api.delete(`/beneficiaries/${id}`);
      toast.success('Deleted');
      fetchData();
    } catch { toast.error('Failed to delete'); }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(
        '/beneficiaries/import?auto_match=true&min_score=80',
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      const d = res.data;
      const autoMsg = d.auto_linked > 0 ? ` · Auto-linked ${d.auto_linked} to suppliers` : '';
      toast.success(`Imported ${d.imported} · Skipped ${d.skipped} of ${d.total_rows}${autoMsg}`);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Import failed'); }
    finally { setImporting(false); e.target.value = ''; }
  };

  const loadMatchSuggestions = async () => {
    setShowMatch(true);
    setMatchLoading(true);
    setSelectedMatches({});
    try {
      const res = await api.get('/beneficiaries/match-suggestions');
      setMatchData(res.data);
      const pre = {};
      for (const s of res.data.suggestions || []) {
        const top = s.top_matches?.[0];
        if (top) {
          // Encode both link_type and link_id into a single Select value
          // so the dropdown can toggle between match types in one widget.
          // Format: "<link_type>:<link_id>". Back-compat: if the response
          // only has supplier_id (pre-multi-entity deploy), we fall back.
          pre[s.beneficiary_id] = top.link_type && top.link_id
            ? `${top.link_type}:${top.link_id}`
            : (top.supplier_id ? `supplier:${top.supplier_id}` : '');
        }
      }
      setSelectedMatches(pre);
    } catch { toast.error('Failed to load suggestions'); }
    finally { setMatchLoading(false); }
  };

  const applyBulkLink = async () => {
    const links = Object.entries(selectedMatches)
      .filter(([, val]) => val)
      .map(([beneficiary_id, val]) => {
        // Value is either "link_type:link_id" (new) or a bare supplier_id
        // (legacy). Parse defensively so we stay compatible during rollout.
        const [link_type, link_id] = (val || '').includes(':')
          ? val.split(':')
          : ['supplier', val];
        return { beneficiary_id, link_type, link_id };
      });
    if (!links.length) { toast.error('Select at least one match'); return; }
    try {
      const res = await api.post('/beneficiaries/bulk-link', { links });
      toast.success(`Linked ${res.data.linked} beneficiaries`);
      setShowMatch(false);
      fetchData();
    } catch { toast.error('Bulk-link failed'); }
  };

  const filtered = useMemo(() => {
    return items.filter(b => {
      if (filterLink === 'linked' && !b.link_type) return false;
      if (filterLink === 'unlinked' && b.link_type) return false;
      if (!['all', 'linked', 'unlinked'].includes(filterLink) && b.link_type !== filterLink) return false;
      if (search) {
        const q = search.toLowerCase();
        return (b.name || '').toLowerCase().includes(q)
          || (b.iban || '').toLowerCase().includes(q)
          || (b.account_number || '').toLowerCase().includes(q)
          || (b.bank || '').toLowerCase().includes(q)
          || (b.link_name || '').toLowerCase().includes(q);
      }
      return true;
    });
  }, [items, filterLink, search]);

  const stats = useMemo(() => {
    const total = items.length;
    const linked = items.filter(b => b.link_type).length;
    const withIban = items.filter(b => b.iban).length;
    return { total, linked, unlinked: total - linked, withIban };
  }, [items]);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="beneficiaries-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg shadow-indigo-200/50">
              <Users size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground" data-testid="page-title">Beneficiaries</h1>
              <p className="text-sm text-muted-foreground mt-0.5">Bank-statement counterparties linked to suppliers, customers, employees or partners</p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" className="rounded-xl" onClick={loadMatchSuggestions} data-testid="match-suggestions-btn">
              <Sparkles size={14} className="mr-1.5" /> Auto-match
            </Button>
            <label className="inline-flex items-center">
              <input type="file" accept=".csv,.xlsx,.xls" onChange={handleImport} className="hidden" data-testid="import-file-input" />
              <span className="inline-flex items-center h-9 px-3 rounded-xl border border-input bg-background hover:bg-accent text-sm font-medium cursor-pointer">
                {importing ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Upload size={14} className="mr-1.5" />}
                Import CSV / XLSX
              </span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="file" accept=".csv,.xlsx,.xls"
                onChange={async (e) => {
                  const f = e.target.files?.[0]; if (!f) return;
                  const fd = new FormData(); fd.append('file', f);
                  try {
                    setImporting(true);
                    const r = await api.post('/beneficiaries/smart-reimport', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                    toast.success(`Smart re-import done: fixed ${r.data.fixed}, new ${r.data.inserted}`);
                    fetchData();
                  } catch (err) { toast.error(err.response?.data?.detail || 'Smart re-import failed'); }
                  finally { setImporting(false); e.target.value = ''; }
                }}
                className="hidden" data-testid="smart-reimport-file-input"
              />
              <span className="inline-flex items-center h-9 px-3 rounded-xl border border-amber-300 bg-amber-50 hover:bg-amber-100 dark:bg-amber-900/20 dark:border-amber-800 text-amber-800 dark:text-amber-300 text-sm font-medium cursor-pointer">
                <AlertTriangle size={14} className="mr-1.5" /> Smart Re-import
              </span>
            </label>
            <Button onClick={openNew} className="rounded-xl" data-testid="new-beneficiary-btn">
              <Plus size={14} className="mr-1.5" /> Add Beneficiary
            </Button>
            <Button
              variant="outline"
              className="rounded-xl border-rose-300 bg-rose-50 hover:bg-rose-100 text-rose-700 dark:bg-rose-900/20 dark:border-rose-800 dark:text-rose-300"
              onClick={() => { setSplitFor({ beneficiaryId: null, defaultCategory: '' }); setShowSplitDialog(true); }}
              data-testid="split-expense-btn"
            >
              <Receipt size={14} className="mr-1.5" /> Split Expense
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard icon={Users} label="Total" value={stats.total} color="text-info" bg="bg-info/10" testid="stat-total" />
          <StatCard icon={Link2} label="Linked" value={stats.linked} color="text-success" bg="bg-success/10" testid="stat-linked" />
          <StatCard icon={AlertCircle} label="Unlinked" value={stats.unlinked} color="text-warning" bg="bg-warning/10" testid="stat-unlinked" />
          <StatCard icon={Wallet} label="With IBAN" value={stats.withIban} color="text-purple-600" bg="bg-purple-100 dark:bg-purple-900/20" testid="stat-iban" />
        </div>

        {(() => {
          // Detect rows whose name is ONLY made of `?` chars (telltale of a CP1256/UTF-8
          // import that lost the original encoding). Show a single dismissible banner
          // when ≥10% of rows are corrupted, pointing the user to re-import.
          const corrupted = items.filter(b => {
            const n = (b.name || '').trim();
            return n.length > 0 && /^[?\s]+$/.test(n);
          });
          const ratio = items.length > 0 ? corrupted.length / items.length : 0;
          if (ratio < 0.10) return null;
          return (
            <div className="flex items-start gap-3 rounded-xl border border-amber-300 dark:border-amber-800 bg-amber-50/80 dark:bg-amber-900/20 px-4 py-3" data-testid="arabic-corruption-banner">
              <AlertTriangle size={18} className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
                  {corrupted.length} beneficiaries have unreadable Arabic names
                </p>
                <p className="text-xs text-amber-800 dark:text-amber-300/80 mt-0.5">
                  These rows show "????" because the file was imported before the encoding fix landed. Click <span className="font-semibold">Repair with original file</span> below — we'll match each row by IBAN/account number and overwrite ONLY the names. No data loss, no duplicates, no need to delete first.
                </p>
              </div>
              {/* One-click repair: opens the same file picker as Smart Re-import
                  but lives INSIDE the banner so the user doesn't have to scan
                  the toolbar to find it. */}
              <label className="inline-flex items-center flex-shrink-0">
                <input
                  type="file" accept=".csv,.xlsx,.xls"
                  onChange={async (e) => {
                    const f = e.target.files?.[0]; if (!f) return;
                    const fd = new FormData(); fd.append('file', f);
                    try {
                      setImporting(true);
                      const r = await api.post('/beneficiaries/smart-reimport', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                      toast.success(`Repaired: ${r.data.fixed} names fixed · ${r.data.inserted} new added`);
                      fetchData();
                    } catch (err) { toast.error(err.response?.data?.detail || 'Repair failed'); }
                    finally { setImporting(false); e.target.value = ''; }
                  }}
                  className="hidden"
                  data-testid="banner-repair-file-input"
                />
                <span className="inline-flex items-center h-9 px-3 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold cursor-pointer shadow-sm">
                  {importing ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Upload size={13} className="mr-1.5" />}
                  Repair with original file
                </span>
              </label>
              <Button
                size="sm"
                variant="ghost"
                className="text-amber-700 hover:bg-amber-100 dark:hover:bg-amber-900/40 h-9 text-xs flex-shrink-0"
                onClick={() => {
                  setSelectedIds(new Set(corrupted.map(b => b.id)));
                  toast.info(`Selected ${corrupted.length} corrupted rows — use bulk Delete if you'd rather start fresh.`);
                }}
                data-testid="select-corrupted-btn"
              >
                Or select to delete ({corrupted.length})
              </Button>
            </div>
          );
        })()}

        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search by name / IBAN / account / bank / linked party"
              className="pl-9 h-9 text-sm rounded-xl" data-testid="search-input"
            />
          </div>
          <Select value={filterLink} onValueChange={setFilterLink}>
            <SelectTrigger className="w-48 h-9 text-sm rounded-xl" data-testid="filter-link"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="linked">Linked only</SelectItem>
              <SelectItem value="unlinked">Unlinked only</SelectItem>
              {LINK_TYPES.map(t => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Badge variant="outline" className="h-9 px-3 text-xs rounded-xl">{filtered.length} shown</Badge>
          {selectedIds.size > 0 && (
            <Button
              size="sm"
              variant="destructive"
              className="h-9 rounded-xl"
              onClick={handleBulkDelete}
              data-testid="bulk-delete-btn"
            >
              <Trash2 size={13} className="mr-1.5" /> Delete ({selectedIds.size})
            </Button>
          )}
        </div>

        <Card className="border-0 shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_1px_4px_rgba(0,0,0,0.04)] overflow-hidden rounded-2xl">
          <CardContent className="p-0">
            {loading ? (
              <div className="py-20 flex items-center justify-center text-muted-foreground">
                <Loader2 className="animate-spin mr-2" size={18} /> Loading…
              </div>
            ) : filtered.length === 0 ? (
              <div className="py-20 text-center" data-testid="empty-state">
                <Users size={40} className="mx-auto mb-3 opacity-20" />
                <p className="text-sm text-muted-foreground">
                  {items.length === 0 ? 'No beneficiaries yet' : 'No match for current filters'}
                </p>
                {items.length === 0 && (
                  <Button onClick={openNew} variant="outline" size="sm" className="mt-3 rounded-xl">
                    <Plus size={14} className="mr-1.5" /> Add your first beneficiary
                  </Button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="beneficiaries-table">
                  <thead>
                    <tr className="border-b bg-muted/50 text-muted-foreground">
                      <th className="p-3 w-10">
                        <button
                          onClick={() => toggleSelectAll(filtered)}
                          className="flex items-center justify-center hover:text-foreground"
                          data-testid="select-all-toggle"
                          aria-label="Select all"
                        >
                          {filtered.length > 0 && filtered.every(r => selectedIds.has(r.id))
                            ? <CheckSquare size={15} className="text-primary" />
                            : <Square size={15} />}
                        </button>
                      </th>
                      <th className="text-left p-3 font-medium">Name</th>
                      <th className="text-left p-3 font-medium">Bank</th>
                      <th className="text-left p-3 font-medium">IBAN</th>
                      <th className="text-left p-3 font-medium">Account #</th>
                      <th className="text-left p-3 font-medium">Linked to</th>
                      <th className="text-right p-3 font-medium">Balance</th>
                      <th className="text-right p-3 font-medium w-24">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(b => {
                      const lt = LINK_MAP[b.link_type];
                      const isSelected = selectedIds.has(b.id);
                      return (
                        <tr
                          key={b.id}
                          className={`border-b hover:bg-muted/50 transition-colors ${isSelected ? 'bg-primary/5' : ''}`}
                          data-testid={`beneficiary-row-${b.id}`}
                        >
                          <td className="p-3">
                            <button
                              onClick={() => toggleSelect(b.id)}
                              className="flex items-center justify-center hover:text-foreground"
                              data-testid={`select-${b.id}`}
                              aria-label={isSelected ? 'Deselect' : 'Select'}
                            >
                              {isSelected
                                ? <CheckSquare size={15} className="text-primary" />
                                : <Square size={15} className="text-muted-foreground" />}
                            </button>
                          </td>
                          <td className="p-3 font-medium" lang="ar" dir="auto">
                            <div className="flex items-center gap-1.5">
                              <span>{b.name}</span>
                              {/* Ambiguity badge — the server stamps
                                  `last_match_ambiguity_count > 1` on
                                  beneficiaries whose name collided with
                                  another at auto-match time. Opens a
                                  Popover listing the name-twins so an
                                  admin can investigate & merge. */}
                              {(b.last_match_ambiguity_count || 0) > 1 && (
                                <AmbiguityBadge beneficiary={b} />
                              )}
                            </div>
                          </td>
                          <td className="p-3 text-muted-foreground" lang="ar" dir="auto">{b.bank || '—'}</td>
                          <td className="p-3 font-mono text-[11px] text-muted-foreground">{b.iban || '—'}</td>
                          <td className="p-3 font-mono text-[11px] text-muted-foreground">{b.account_number || '—'}</td>
                          <td className="p-3">
                            {lt ? (
                              <div className="flex items-center gap-1.5">
                                <span className={`text-[10px] font-medium px-2 py-0.5 rounded-md border ${lt.color}`}>{lt.label}</span>
                                <span className="text-xs text-foreground">{b.link_name || '—'}</span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span className="text-[11px] text-muted-foreground italic">Unlinked</span>
                                {/* iter395n — Inline suggested-link chip.
                                    Only shows when /match-suggestions
                                    returned a top match for this row.
                                    Clicking opens the one-step confirm
                                    dialog. Lets users approve matches
                                    without leaving the table. */}
                                {inlineSuggestions[b.id] && (
                                  <button
                                    type="button"
                                    onClick={() => setConfirmLink({ beneficiary: b, match: inlineSuggestions[b.id] })}
                                    className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-md bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900/50 text-emerald-800 dark:text-emerald-200 hover:bg-emerald-100 dark:hover:bg-emerald-950/50 font-semibold transition-colors max-w-[220px]"
                                    data-testid={`suggested-link-${b.id}`}
                                    title={`Suggested: link to ${inlineSuggestions[b.id].link_type} — ${inlineSuggestions[b.id].entity_name} (${inlineSuggestions[b.id].score}% · ${inlineSuggestions[b.id].reason})`}
                                  >
                                    <span className="opacity-60 uppercase tracking-wider shrink-0">{(inlineSuggestions[b.id].link_type || '?')[0]}</span>
                                    <span className="truncate">{inlineSuggestions[b.id].entity_name}</span>
                                    <span className="opacity-60 shrink-0">({inlineSuggestions[b.id].score}%)</span>
                                  </button>
                                )}
                              </div>
                            )}
                          </td>
                          <td className="p-3 text-right tabular-nums" data-testid={`balance-${b.id}`}>
                            {/* Live outstanding balance from the linked entity:
                                 supplier/employee/partner = positive means WE owe them
                                 customer = positive means THEY owe US
                                A null balance means the link_type doesn't have a
                                meaningful balance concept (e.g. expense_category).
                                When the entity has been paid from multiple branches,
                                show a Building2 icon that opens a popover breakdown
                                of who paid how much. */}
                            {b.link_balance == null ? (
                              <span className="text-[11px] text-muted-foreground">—</span>
                            ) : (
                              <div className="flex items-center justify-end gap-1.5">
                                <span className={`text-xs font-semibold ${
                                  b.link_balance > 0
                                    ? (b.link_type === 'customer' ? 'text-emerald-600' : 'text-rose-600')
                                    : 'text-muted-foreground'
                                }`}>
                                  {b.link_balance > 0 ? Math.round(b.link_balance).toLocaleString() : '0'}
                                </span>
                                {Array.isArray(b.link_branch_breakdown) && b.link_branch_breakdown.length > 1 && (
                                  <Popover>
                                    <PopoverTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-6 w-6 text-muted-foreground hover:text-foreground"
                                        data-testid={`branch-breakdown-${b.id}`}
                                        title={`${b.link_branch_breakdown.length} branches have paid this entity — click to see breakdown`}
                                      >
                                        <Building2 size={12} />
                                      </Button>
                                    </PopoverTrigger>
                                    <PopoverContent align="end" className="w-64 p-3" data-testid={`branch-breakdown-content-${b.id}`}>
                                      <div className="space-y-1.5">
                                        <p className="text-xs font-semibold mb-1.5">
                                          Paid by {b.link_branch_breakdown.length} branch{b.link_branch_breakdown.length === 1 ? '' : 'es'}
                                        </p>
                                        {b.link_branch_breakdown.map((row) => (
                                          <div key={row.branch_id || 'none'} className="flex items-center justify-between gap-2 text-xs">
                                            <span className="text-muted-foreground truncate">{row.branch_name || '— No branch —'}</span>
                                            <span className="font-mono tabular-nums font-semibold">{Math.round(row.total).toLocaleString()}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </PopoverContent>
                                  </Popover>
                                )}
                              </div>
                            )}
                          </td>
                          <td className="p-3 text-right">
                            <div className="flex justify-end gap-1">
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setPatternsFor(b)} data-testid={`bank-patterns-${b.id}`} title="Edit bank-code patterns">
                                <Tag size={13} />
                              </Button>
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEdit(b)} data-testid={`edit-${b.id}`}>
                                <Edit2 size={13} />
                              </Button>
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-error" onClick={() => handleDelete(b.id)} data-testid={`delete-${b.id}`}>
                                <Trash2 size={13} />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-lg" data-testid="beneficiary-dialog">
            <DialogHeader>
              <DialogTitle className="font-outfit">{editing ? 'Edit Beneficiary' : 'Add Beneficiary'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSave} className="space-y-3">
              <div>
                <Label className="text-xs">Name *</Label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required data-testid="form-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Bank</Label>
                  <Input value={form.bank} onChange={e => setForm({ ...form, bank: e.target.value })} placeholder="e.g. Al Rajhi" data-testid="form-bank" />
                </div>
                <div>
                  <Label className="text-xs">Account Number</Label>
                  <Input value={form.account_number} onChange={e => setForm({ ...form, account_number: e.target.value })} data-testid="form-account" />
                </div>
              </div>
              <div>
                <Label className="text-xs">IBAN</Label>
                <Input value={form.iban} onChange={e => setForm({ ...form, iban: e.target.value })} className="font-mono" placeholder="SA..." data-testid="form-iban" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Link Type</Label>
                  <Select value={form.link_type || 'none'} onValueChange={v => setForm({ ...form, link_type: v === 'none' ? '' : v, link_id: '' })}>
                    <SelectTrigger data-testid="form-link-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Unlinked —</SelectItem>
                      {LINK_TYPES.map(t => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Linked Entity</Label>
                  <Select
                    value={form.link_id || ''}
                    onValueChange={v => setForm({ ...form, link_id: v })}
                    disabled={!form.link_type || !!LINK_MAP[form.link_type]?.tagOnly}
                  >
                    <SelectTrigger data-testid="form-link-id">
                      <SelectValue placeholder={
                        !form.link_type ? 'Pick type first'
                        : LINK_MAP[form.link_type]?.tagOnly ? 'Tag-only (no entity needed)'
                        : 'Select…'
                      } />
                    </SelectTrigger>
                    <SelectContent>
                      {linkOptions.map(o => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs">Notes</Label>
                <Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Optional" data-testid="form-notes" />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
                <Button type="submit" data-testid="save-beneficiary-btn">{editing ? 'Update' : 'Create'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={showMatch} onOpenChange={setShowMatch}>
          <DialogContent className="max-w-3xl max-h-[80dvh] overflow-hidden flex flex-col" data-testid="match-dialog">
            <DialogHeader>
              <DialogTitle className="font-outfit flex items-center gap-2">
                <Sparkles size={18} className="text-purple-600" /> Auto-match Beneficiaries
              </DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto">
              {matchLoading ? (
                <div className="py-12 flex items-center justify-center text-muted-foreground">
                  <Loader2 className="animate-spin mr-2" size={18} /> Finding best matches…
                </div>
              ) : !matchData ? null : matchData.suggestions.length === 0 ? (
                <div className="py-12 text-center">
                  <CheckCircle2 size={40} className="mx-auto mb-3 text-success opacity-60" />
                  <p className="text-sm text-muted-foreground">
                    {matchData.total_unlinked === 0
                      ? 'All beneficiaries are already linked'
                      : `No confident suggestions found for ${matchData.total_unlinked} unlinked beneficiaries`}
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="text-xs text-muted-foreground px-1">
                    {matchData.with_suggestions} of {matchData.total_unlinked} unlinked beneficiaries have suggested matches across
                    {' '}suppliers ({matchData.totals?.supplier || 0}),
                    {' '}customers ({matchData.totals?.customer || 0}),
                    {' '}employees ({matchData.totals?.employee || 0}),
                    {' '}and partners ({matchData.totals?.partner || 0}).
                    Review and Apply.
                  </div>
                  {matchData.suggestions.map(s => (
                    <div key={s.beneficiary_id} className="p-3 border border-border rounded-xl flex items-center gap-3 flex-wrap" data-testid={`match-row-${s.beneficiary_id}`}>
                      <div className="flex-1 min-w-[180px]">
                        <p className="text-sm font-medium">{s.beneficiary_name}</p>
                        <p className="text-[11px] font-mono text-muted-foreground">{s.iban || '—'} · {s.bank || '—'}</p>
                      </div>
                      <Select
                        value={selectedMatches[s.beneficiary_id] || '__skip__'}
                        onValueChange={v => setSelectedMatches({ ...selectedMatches, [s.beneficiary_id]: v === '__skip__' ? '' : v })}
                      >
                        <SelectTrigger className="w-72 h-9 text-xs" data-testid={`match-select-${s.beneficiary_id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__skip__">— Skip —</SelectItem>
                          {s.top_matches.map((m) => {
                            // Encode link_type + link_id into a single value
                            // so the Select can switch between match types.
                            const val = m.link_type && m.link_id
                              ? `${m.link_type}:${m.link_id}`
                              : (m.supplier_id ? `supplier:${m.supplier_id}` : '');
                            const label = m.entity_name || m.supplier_name || '(unnamed)';
                            const type = m.link_type || 'supplier';
                            return (
                              <SelectItem key={val} value={val}>
                                <span className="inline-flex items-center gap-2">
                                  <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-semibold">
                                    {type}
                                  </span>
                                  {label} ({m.score}% · {m.reason})
                                </span>
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowMatch(false)}>Close</Button>
              {matchData?.suggestions?.length > 0 && (
                <Button onClick={applyBulkLink} data-testid="apply-bulk-link-btn">
                  <Link2 size={14} className="mr-1.5" /> Apply selected
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <MultiPersonExpenseDialog
        open={showSplitDialog}
        onOpenChange={setShowSplitDialog}
        beneficiaryId={splitFor.beneficiaryId}
        defaultCategory={splitFor.defaultCategory}
        onCreated={() => { /* could refetch beneficiary stats here */ }}
      />

      {/* iter395n — One-click suggested-link confirm dialog. Triggered
          by the inline emerald chip on each unlinked row. Calls the
          existing /beneficiaries/bulk-link endpoint with a single link
          so we reuse the same backend code path as the multi-row wizard. */}
      <Dialog open={!!confirmLink} onOpenChange={(o) => !o && setConfirmLink(null)}>
        <DialogContent className="max-w-md" data-testid="suggested-link-dialog">
          <DialogHeader>
            <DialogTitle>Confirm link</DialogTitle>
            <DialogDescription>
              Link this beneficiary to the suggested entity?
            </DialogDescription>
          </DialogHeader>
          {confirmLink && (
            <div className="space-y-3 py-2">
              <div className="rounded-lg border border-border p-3 bg-muted/30">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Beneficiary</div>
                <div className="font-medium" lang="ar" dir="auto">{confirmLink.beneficiary.name}</div>
                <div className="text-[10px] text-muted-foreground mt-0.5 font-mono">{confirmLink.beneficiary.iban || confirmLink.beneficiary.account_number || '—'}</div>
              </div>
              <div className="text-center text-muted-foreground text-xs">↓</div>
              <div className="rounded-lg border-2 border-emerald-300 dark:border-emerald-800 bg-emerald-50/40 dark:bg-emerald-950/20 p-3">
                <div className="text-[10px] uppercase tracking-wider text-emerald-700 dark:text-emerald-300 font-semibold mb-1">
                  Suggested {confirmLink.match.link_type} · {confirmLink.match.score}% match · {confirmLink.match.reason}
                </div>
                <div className="font-bold text-emerald-900 dark:text-emerald-100">{confirmLink.match.entity_name}</div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmLink(null)} data-testid="suggested-link-cancel">Cancel</Button>
            <Button
              data-testid="suggested-link-confirm"
              onClick={async () => {
                try {
                  await api.post('/beneficiaries/bulk-link', {
                    links: [{
                      beneficiary_id: confirmLink.beneficiary.id,
                      link_type: confirmLink.match.link_type,
                      link_id: confirmLink.match.link_id,
                    }],
                  });
                  toast.success(`Linked to ${confirmLink.match.entity_name}`);
                  // Drop this row from inline suggestions (it's no longer unlinked).
                  setInlineSuggestions((prev) => {
                    const next = { ...prev };
                    delete next[confirmLink.beneficiary.id];
                    return next;
                  });
                  setConfirmLink(null);
                  fetchData();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Failed to link');
                }
              }}
            >
              Confirm link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* iter469 — Bank-code patterns dialog. Mount only when active so the
          inner data fetch only fires for the row the user clicked. */}
      {patternsFor && (
        <BankCodePatternsDialog
          beneficiary={patternsFor}
          onClose={() => setPatternsFor(null)}
        />
      )}
    </DashboardLayout>
  );
}

function StatCard({ icon: Icon, label, value, color, bg, testid }) {
  return (
    <Card data-testid={testid}>
      <CardContent className="pt-4 pb-3">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${bg}`}><Icon size={18} className={color} /></div>
          <div>
            <div className="text-2xl font-bold font-outfit">{value}</div>
            <div className="text-xs text-muted-foreground">{label}</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * AmbiguityBadge — tiny warning pill that appears next to a beneficiary
 * name when the server flagged `last_match_ambiguity_count > 1`. Clicking
 * the badge opens a Popover that lazy-loads the other beneficiaries
 * sharing this one's name so an admin can decide whether to merge or
 * rename.
 */
function AmbiguityBadge({ beneficiary }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [candidates, setCandidates] = useState(null);

  const loadCandidates = async () => {
    if (candidates) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/beneficiaries/${beneficiary.id}/ambiguity-candidates`);
      setCandidates(data.candidates || []);
    } catch {
      setCandidates([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (v) loadCandidates(); }}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-5 w-5 p-0 text-amber-600 hover:text-amber-700 hover:bg-amber-100"
          data-testid={`ambiguity-badge-${beneficiary.id}`}
          title={`Name collides with ${(beneficiary.last_match_ambiguity_count || 1) - 1} other beneficiary — click to review`}
        >
          <AlertTriangle size={12} />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-80 p-3"
        data-testid={`ambiguity-content-${beneficiary.id}`}
      >
        <div className="text-xs font-semibold mb-2 flex items-center gap-1.5 text-amber-700">
          <AlertTriangle size={13} /> Ambiguous name match
        </div>
        <p className="text-[11px] text-muted-foreground mb-3">
          <strong>{beneficiary.name}</strong> shares its name with{' '}
          {(beneficiary.last_match_ambiguity_count || 1) - 1} other beneficiary.
          The server picked this one at auto-match time; review the twins below
          and merge/rename if needed.
        </p>
        {loading ? (
          <div className="text-xs text-muted-foreground py-2 text-center">Loading…</div>
        ) : (candidates || []).length === 0 ? (
          <div className="text-xs text-muted-foreground italic py-2">
            No other beneficiaries with this name (they may have been merged).
          </div>
        ) : (
          <div className="space-y-1.5">
            {candidates.map((c) => (
              <div
                key={c.id}
                className="flex items-center justify-between gap-2 rounded-md border border-amber-200 bg-amber-50/50 px-2 py-1.5"
              >
                <div className="text-xs min-w-0 flex-1">
                  <div className="font-mono text-[10px] truncate text-muted-foreground">{c.iban || '(no IBAN)'}</div>
                  <div className="text-foreground truncate">
                    {c.link_type ? (
                      <span><span className="uppercase text-[9px] font-semibold text-muted-foreground">{c.link_type}</span> · {c.link_name || '(unlinked)'}</span>
                    ) : (
                      <span className="italic text-muted-foreground">Unlinked</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}




// ── iter469 — BankCodePatternsDialog ─────────────────────────────
// Manages the `bank_code_patterns` array on a single beneficiary.
// Each pattern is a substring that, when found inside a bank-statement
// description, attaches its classification (category, POS flag, refund
// flag, branch, account hint) to the matched row.
function BankCodePatternsDialog({ beneficiary, onClose }) {
  const [patterns, setPatterns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [branches, setBranches] = useState([]);
  // iter469 — Auto-suggest state
  const [suggesting, setSuggesting] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  // null = haven't asked yet, [] = asked and none, [...] = candidates

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [pRes, bRes] = await Promise.all([
          api.get(`/beneficiaries/${beneficiary.id}/bank-patterns`),
          api.get('/branches'),
        ]);
        if (!active) return;
        setPatterns(pRes.data?.patterns || []);
        const bs = Array.isArray(bRes.data) ? bRes.data : (bRes.data?.data || []);
        setBranches(bs);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Failed to load patterns');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [beneficiary.id]);

  const addRow = () => {
    setPatterns(p => [
      ...p,
      { pattern: '', category: '', is_pos: false, is_refund: false, branch_id: '', account: '', notes: '' },
    ]);
  };
  const updateRow = (idx, patch) => {
    setPatterns(p => p.map((r, i) => i === idx ? { ...r, ...patch } : r));
  };
  const removeRow = (idx) => {
    setPatterns(p => p.filter((_, i) => i !== idx));
  };

  // iter469 — Hit the suggest endpoint, then let the user tick which to add.
  const runSuggest = async () => {
    setSuggesting(true);
    try {
      const res = await api.get(`/beneficiaries/${beneficiary.id}/suggest-patterns`, {
        params: { days: 90, limit: 5 },
      });
      const list = (res.data?.suggestions || []).map(s => ({ ...s, _selected: true }));
      setSuggestions(list);
      if (list.length === 0) {
        toast.info(res.data?.note || 'No recurring patterns found in the last 90 days.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Suggest failed');
    } finally {
      setSuggesting(false);
    }
  };

  const acceptSelectedSuggestions = () => {
    const sel = (suggestions || []).filter(s => s._selected);
    if (sel.length === 0) {
      toast.error('Tick at least one suggestion');
      return;
    }
    const newRows = sel.map(s => ({
      pattern: s.pattern,
      category: s.suggested_category || '',
      is_pos: !!s.is_pos,
      is_refund: !!s.is_refund,
      branch_id: '',
      account: '',
      notes: `Auto-suggested · ${s.count}× in last 90d`,
    }));
    setPatterns(p => {
      const existingKeys = new Set(p.map(r => (r.pattern || '').toUpperCase()));
      return [...p, ...newRows.filter(r => !existingKeys.has(r.pattern.toUpperCase()))];
    });
    setSuggestions(null);
    toast.success(`Added ${newRows.length} suggestion(s) — review and click "Save patterns" to persist.`);
  };

  const save = async () => {
    const cleaned = patterns.filter(p => (p.pattern || '').trim());
    if (cleaned.length !== patterns.length) {
      // Drop empty rows silently.
    }
    setSaving(true);
    try {
      const res = await api.put(`/beneficiaries/${beneficiary.id}/bank-patterns`, {
        patterns: cleaned,
      });
      toast.success(`Saved ${res.data?.count ?? cleaned.length} pattern(s)`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // A small list of common categories. Free-text is also allowed.
  const CATEGORIES = [
    'POS Settlement', 'POS Refund', 'Bank Fee', 'Salary',
    'Supplies', 'Fuel', 'Rent', 'Utilities', 'Government', 'VAT',
    'Customer Payment', 'Supplier Payment', 'Online Sale',
    'Transfer', 'Other',
  ];

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85dvh] overflow-y-auto" data-testid="bank-patterns-dialog">
        <DialogHeader>
          <DialogTitle className="font-outfit flex items-center gap-2">
            <Tag size={16} className="text-info" />
            Bank-code patterns — {beneficiary.name}
          </DialogTitle>
          <DialogDescription>
            When a bank-statement description contains one of these patterns,
            the row is auto-tagged with this beneficiary&apos;s category, POS /
            refund flag, branch, and account. Used by Bank Statements,
            Banking Hub, and reconciliation views.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-10 text-center text-muted-foreground">
            <Loader2 size={18} className="inline animate-spin mr-2" /> Loading…
          </div>
        ) : (
          <div className="space-y-2">
            {/* iter469 — Auto-suggest panel */}
            <div className="flex items-center justify-between gap-2 p-2 rounded-lg bg-indigo-50/60 border border-indigo-200">
              <div className="text-xs text-indigo-900 leading-tight">
                <strong>Auto-suggest:</strong> scan the last 90 days of bank-statement descriptions
                linked to this beneficiary and propose the top recurring substrings as patterns.
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={runSuggest}
                disabled={suggesting}
                className="bg-white shrink-0"
                data-testid="pattern-suggest-btn"
              >
                {suggesting ? <Loader2 size={13} className="animate-spin mr-1" /> : <Sparkles size={13} className="mr-1" />}
                Suggest from past statements
              </Button>
            </div>

            {suggestions !== null && suggestions.length > 0 && (
              <div className="border border-indigo-300 rounded-xl p-2.5 bg-indigo-50/40" data-testid="suggestions-panel">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="text-xs font-semibold text-indigo-900">
                    {suggestions.length} suggestion(s) — tick to add:
                  </div>
                  <button className="text-xs text-muted-foreground hover:underline" onClick={() => setSuggestions(null)}>Dismiss</button>
                </div>
                <div className="space-y-1.5">
                  {suggestions.map((s, i) => (
                    <label
                      key={`${s.pattern}-${i}`}
                      className="flex items-center gap-2 p-1.5 rounded-md bg-white cursor-pointer hover:bg-indigo-50"
                      data-testid={`suggestion-${i}`}
                    >
                      <input
                        type="checkbox"
                        checked={s._selected}
                        onChange={(e) => setSuggestions(arr => arr.map((x, j) => j === i ? { ...x, _selected: e.target.checked } : x))}
                      />
                      <span className="font-mono text-xs text-foreground">{s.pattern}</span>
                      <Badge variant="outline" className="text-[10px]">{s.count}×</Badge>
                      {s.suggested_category && <Badge variant="outline" className="text-[10px] bg-info/10">{s.suggested_category}</Badge>}
                      {s.is_pos && <Badge variant="outline" className="text-[10px] bg-amber-100 text-amber-800">POS</Badge>}
                      {s.is_refund && <Badge variant="outline" className="text-[10px] bg-rose-100 text-rose-800">Refund</Badge>}
                    </label>
                  ))}
                </div>
                <div className="flex justify-end mt-2">
                  <Button size="sm" onClick={acceptSelectedSuggestions} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="suggestions-accept-btn">
                    Add selected to patterns
                  </Button>
                </div>
              </div>
            )}

            {patterns.length === 0 && suggestions === null && (
              <div className="text-xs text-muted-foreground p-6 text-center border border-dashed rounded-xl">
                No patterns yet. Click <strong>Suggest from past statements</strong> above, or add the first one manually — e.g. <span className="font-mono">&quot;MADA POS&quot;</span> tagged as POS Settlement.
              </div>
            )}
            {patterns.map((p, idx) => (
              <div key={idx} className="border border-border rounded-xl p-3 grid grid-cols-12 gap-2 items-start" data-testid={`pattern-row-${idx}`}>
                <div className="col-span-3">
                  <Label className="text-[10px] uppercase text-muted-foreground">Pattern (substring)</Label>
                  <Input
                    className="h-8 text-xs font-mono uppercase"
                    placeholder="e.g. MADA POS"
                    value={p.pattern || ''}
                    onChange={(e) => updateRow(idx, { pattern: e.target.value.toUpperCase() })}
                    data-testid={`pattern-input-${idx}`}
                  />
                </div>
                <div className="col-span-3">
                  <Label className="text-[10px] uppercase text-muted-foreground">Category</Label>
                  <Select value={p.category || ''} onValueChange={(v) => updateRow(idx, { category: v })}>
                    <SelectTrigger className="h-8 text-xs" data-testid={`pattern-category-${idx}`}>
                      <SelectValue placeholder="Pick…" />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label className="text-[10px] uppercase text-muted-foreground">Branch (optional)</Label>
                  <Select value={p.branch_id || 'none'} onValueChange={(v) => updateRow(idx, { branch_id: v === 'none' ? '' : v })}>
                    <SelectTrigger className="h-8 text-xs" data-testid={`pattern-branch-${idx}`}>
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— None —</SelectItem>
                      {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-3">
                  <Label className="text-[10px] uppercase text-muted-foreground">Account hint</Label>
                  <Input
                    className="h-8 text-xs"
                    placeholder="e.g. POS Clearing"
                    value={p.account || ''}
                    onChange={(e) => updateRow(idx, { account: e.target.value })}
                    data-testid={`pattern-account-${idx}`}
                  />
                </div>
                <div className="col-span-1 flex items-end justify-end pt-3">
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-error" onClick={() => removeRow(idx)} data-testid={`pattern-remove-${idx}`}>
                    <Trash2 size={13} />
                  </Button>
                </div>

                <div className="col-span-12 flex items-center gap-4 pl-1 pt-1">
                  <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                    <input type="checkbox" checked={!!p.is_pos} onChange={(e) => updateRow(idx, { is_pos: e.target.checked })} data-testid={`pattern-pos-${idx}`} />
                    Is POS settlement
                  </label>
                  <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                    <input type="checkbox" checked={!!p.is_refund} onChange={(e) => updateRow(idx, { is_refund: e.target.checked })} data-testid={`pattern-refund-${idx}`} />
                    Is Refund / Chargeback
                  </label>
                  <Input
                    className="h-7 text-xs flex-1"
                    placeholder="Notes (optional)"
                    value={p.notes || ''}
                    onChange={(e) => updateRow(idx, { notes: e.target.value })}
                    data-testid={`pattern-notes-${idx}`}
                  />
                </div>
              </div>
            ))}
            <Button size="sm" variant="outline" onClick={addRow} className="w-full" data-testid="pattern-add-btn">
              <Plus size={13} className="mr-1" /> Add pattern
            </Button>
          </div>
        )}

        <DialogFooter className="mt-3">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving || loading} data-testid="pattern-save-btn">
            {saving ? <><Loader2 size={13} className="animate-spin mr-1" /> Saving…</> : 'Save patterns'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
